import { memo, useEffect } from 'react'
import { theme } from '@carto/meridian-ds/theme'
import { MapState } from '@carto/kepler.gl'

import { useAppSelector, useAppStore } from 'app/state/hooks'
import { MapCommentResponse, MapCommentWithResponses } from 'features/maps/types'
import { CommentTextFieldValue } from 'features/collaboration/ui/comments/CommentTextField'
import CommentPopoverContent from 'features/collaboration/ui/comments/CommentPopoverContent'

import { Comment as CommentType, CommentResponse, CommentAction } from 'features/collaboration/types'

import {
  createMapCommentResponse,
  deleteMapCommentResponse,
  markThreadAsRead,
  createMapComment,
  updateComment,
  updateCommentResponse,
  handleCommentAction
} from 'features/builder/model/mapComments'
import { updateActiveComment } from 'features/builder/state/mapCommentsSlice'
import MapPopover from '../common/MapPopover'
import { useMapCommentWithResponses } from 'features/builder/model/mapCommentsSync'
import { COMMENT_MARKER_SIZE } from 'features/collaboration/ui/comments/CommentMarker'
import { useDragMapState } from 'features/builder/hooks/builderHooks'

export const MIN_COMMENT_POPOVER_HEIGHT = theme.spacingValue * 8
const POPOVER_OFFSET_X = COMMENT_MARKER_SIZE + theme.spacingValue * 0.5
const POPOVER_OFFSET_Y = theme.spacingValue * 1

type MapCommentPopoverProps = {
  activeComment: Partial<MapCommentWithResponses>
  mapState: MapState
  mapContainer?: HTMLElement | null
}

const popoverOffset = { x: POPOVER_OFFSET_X, y: POPOVER_OFFSET_Y }

function MapCommentPopoverComponent({
  activeComment: _activeComment,
  mapState: mainMapState,
  mapContainer
}: MapCommentPopoverProps) {
  const store = useAppStore()
  const mapId = useAppSelector((state) => state.maps.currentMap?.id) as string
  const currentUserId = useAppSelector((state) => state.users.me?.user_id) as string
  const isMapOwner = useAppSelector((state) => state.maps.currentMap?.ownerId) === currentUserId
  const users = useAppSelector((state) => state.mapComments.users)

  const mapState = useDragMapState(mainMapState)

  const { loaded: responsesLoaded, comment: activeCommentWithResponses } = useMapCommentWithResponses(_activeComment.id)
  const activeComment = (activeCommentWithResponses || _activeComment) as MapCommentWithResponses

  useEffect(() => {
    if (!activeComment.id || !responsesLoaded) return

    if (activeComment.read === false) {
      markThreadAsRead(activeComment.id, { store })
    }
  }, [activeComment, store, responsesLoaded])

  const handleClose = () => {
    store.dispatch(updateActiveComment(undefined))
  }

  const handleSubmitComment = async (
    newCommentDraft: CommentTextFieldValue,
    editedElement?: CommentResponse | CommentType
  ) => {
    try {
      if (!activeComment.anchor) return

      const savedComment = { ...newCommentDraft, mapId, anchor: activeComment.anchor }

      if (editedElement) {
        const update = { ...editedElement, ...newCommentDraft } as MapCommentWithResponses | MapCommentResponse
        if (!update.mapId && activeComment.mapId) {
          update.mapId = activeComment.mapId
        }
        if ('commentId' in update) {
          await updateCommentResponse(update, { store })
        } else {
          await updateComment(update, { store })
        }
      } else if (!activeComment.id) {
        await createMapComment(savedComment, { store })
      } else {
        const response = {
          commentId: activeComment.id,
          ...newCommentDraft,
          mapId
        }
        await createMapCommentResponse(response, { store })
      }
    } catch (error) {
      console.error(`Failed to save comment: ${error}`, error)
    }
  }

  const onCommentAction = async (action: CommentAction) => {
    if (!activeComment.id) return

    await handleCommentAction(activeComment, action, { store })
  }

  const handleResponseAction = async (response: CommentResponse, action: CommentAction) => {
    if (!activeComment.id || !response.id) return

    switch (action) {
      case 'delete':
        await deleteMapCommentResponse(response.id, activeComment.id, { store })
        break
      default:
        break
    }
  }

  if (!activeComment.anchor || !mapContainer) return null

  return (
    <MapPopover mapState={mapState} position={activeComment.anchor} offset={popoverOffset} mapContainer={mapContainer}>
      <CommentPopoverContent
        currentUserId={currentUserId}
        isOwner={isMapOwner}
        users={users}
        activeComment={activeComment}
        onCommentAction={onCommentAction}
        onResponseAction={handleResponseAction}
        onSubmit={handleSubmitComment}
        onClose={handleClose}
        scrollToLastResponse={responsesLoaded}
      />
    </MapPopover>
  )
}

const MapCommentPopover = memo(MapCommentPopoverComponent)
export default MapCommentPopover
